import React, {useEffect, useMemo} from "react";
import {useProjectTranslations} from "@i18n/i18nUtils";
import {ProductFiltersState} from "@lib/Catalog/CatalogHooks";
import {SelectField, SelectOption} from "@components/Form/SelectField";
import {Controller, useForm, useWatch} from "react-hook-form";
import {useDebouncedEffect} from "@commons/Hooks";
import {TextField} from "@components/Form/TextField";
import {formatPoints} from "@commons/Utils";

type CatalogFiltersProps = {
    categories: string[];
    brands: string[];
    state: ProductFiltersState;
    setState: React.Dispatch<React.SetStateAction<ProductFiltersState>>;
    hasActiveFilters: boolean;
    onReset: () => void;
    minAll: number;
    maxAll: number;
    hideTitle?: boolean;
    markUserFilterChange: () => void;
};

interface FiltersFormValues {
    q: string;
    category: string;
    brand: string;
    minPoints: number;
    maxPoints: number;
}

export default function CatalogFilters({
                                           categories,
                                           brands,
                                           state,
                                           setState,
                                           hasActiveFilters,
                                           onReset,
                                           minAll: minBound,
                                           maxAll: maxBound,
                                           hideTitle = false,
                                           markUserFilterChange
                                       }: CatalogFiltersProps) {

    const {t} = useProjectTranslations();

    const step = useMemo(() => getAlignedNiceStep(minBound, maxBound), [minBound, maxBound]);

    const categoryOptions: SelectOption[] = useMemo(
        () => categories.map((c) => ({value: c, label: c})),
        [categories]
    );

    const brandOptions: SelectOption[] = useMemo(
        () => brands.map((b) => ({value: b, label: b})),
        [brands]
    );

    const form = useForm<FiltersFormValues>({
        mode: "onChange",
        defaultValues: {
            q: state.q ?? "",
            category: state.category ?? "",
            brand: state.brand ?? "",
            minPoints: state.minPoints ?? minBound,
            maxPoints: state.maxPoints ?? maxBound,
        },
    });

    const {control, reset, setValue, getValues} = form;

    useEffect(() => {
        reset({
            q: state.q ?? "",
            category: state.category ?? "",
            brand: state.brand ?? "",
            minPoints: state.minPoints ?? minBound,
            maxPoints: state.maxPoints ?? maxBound,
        });
    }, [state.q, state.category, state.brand, state.minPoints, state.maxPoints, minBound, maxBound, reset]);

    const [q, category, brand, minPoints, maxPoints] = useWatch({
        control,
        name: ["q", "category", "brand", "minPoints", "maxPoints"]
    });

    const firstRangeRef = React.useRef(true);
    useEffect(() => {
        if (firstRangeRef.current) {
            firstRangeRef.current = false;
            return;
        }
        markUserFilterChange();
    }, [minPoints, maxPoints, markUserFilterChange]);

    const firstQRef = React.useRef(true);
    useEffect(() => {
        if (firstQRef.current) {
            firstQRef.current = false;
            return;
        }
        markUserFilterChange();
    }, [q, markUserFilterChange]);

    const hasActiveLocal = useMemo(() => {
        const qq = (q ?? "").trim();
        const cat = (category ?? "").trim();
        const br = (brand ?? "").trim();

        const minV = Number(minPoints ?? minBound);
        const maxV = Number(maxPoints ?? maxBound);

        return (
            qq.length > 0 ||
            cat.length > 0 ||
            br.length > 0 ||
            minV !== minBound ||
            maxV !== maxBound
        );
    }, [q, category, brand, minPoints, maxPoints, minBound, maxBound]);

    useEffect(() => {
        if (hasActiveFilters) return;

        setState((s) => {
            const needs =
                (s.minPoints ?? minBound) !== minBound || (s.maxPoints ?? maxBound) !== maxBound;

            if (!needs) return s;

            return {...s, minPoints: minBound, maxPoints: maxBound};
        });

        setValue("minPoints", minBound, {shouldDirty: false, shouldTouch: false, shouldValidate: false});
        setValue("maxPoints", maxBound, {shouldDirty: false, shouldTouch: false, shouldValidate: false});
    }, [hasActiveFilters, minBound, maxBound, setState, setValue]);

    const firstSelectRef = React.useRef(true);

    useEffect(() => {
        if (firstSelectRef.current) {
            firstSelectRef.current = false;
            return;
        }

        markUserFilterChange();
        setState((s) => ({...s, category: category ?? "", brand: brand ?? ""}));
    }, [category, brand, setState, markUserFilterChange]);

    useDebouncedEffect(() => {
        setState((s) => ({...s, q: (q ?? "").trim()}));
    }, [q], 250);

    useDebouncedEffect(
        () => {
            const nextMin = clamp(
                Number(minPoints ?? minBound),
                minBound,
                Number(maxPoints ?? maxBound)
            );
            const nextMax = clamp(
                Number(maxPoints ?? maxBound),
                Number(minPoints ?? minBound),
                maxBound
            );

            setState((s) => ({...s, minPoints: nextMin, maxPoints: nextMax}));
        },
        [minPoints, maxPoints, minBound, maxBound],
        150
    );

    const handleReset = () => {
        onReset();
        reset({
            q: "",
            category: "",
            brand: "",
            minPoints: minBound,
            maxPoints: maxBound,
        });
        const details = document.querySelector(".catalog__filtersToggle");
        if (details instanceof HTMLDetailsElement) {
            details.open = false;
        }
    };

    return (
        <aside className="catalog__filters" aria-label={t("catalog:filters:title")}>
            {!hideTitle && (
                <div className="catalog__filters__head">
                    <h2 className="tp-m semibold">
                        {t("catalog:filters:title")}
                    </h2>

                    {hasActiveLocal && (
                        <button
                            type="button"
                            className="catalog__filters__reset tp-xs light"
                            onClick={handleReset}
                        >
                            {t("catalog:filters:reset")}
                        </button>
                    )}
                </div>
            )}

            {hideTitle && hasActiveLocal && (
                <div className="catalog__filters__mobileReset">
                    <button
                        type="button"
                        className="catalog__filters__reset tp-xs light"
                        onClick={handleReset}
                    >
                        {t("catalog:filters:reset")}
                    </button>
                </div>
            )}

            <div className="catalog__filters__group">
                <TextField<FiltersFormValues>
                    control={control}
                    name={"q"}
                    label={t("catalog:filters:search:label")}
                    showValidatedIcon={false}
                />
            </div>

            {categories.length > 0 &&
                <div className="catalog__filters__group">
                    <SelectField<FiltersFormValues>
                        control={control}
                        name={"category"}
                        label={t("catalog:filters:category:label")}
                        placeholder={t("catalog:filters:category:label")}
                        options={categoryOptions}
                    />
                </div>
            }

            {brands.length > 0 &&
                <div className="catalog__filters__group">
                    <SelectField<FiltersFormValues>
                        control={control}
                        name={"brand"}
                        label={t("catalog:filters:brand:label")}
                        placeholder={t("catalog:filters:brand:label")}
                        options={brandOptions}
                    />
                </div>
            }

            <div className="catalog__filters__group">

                <div className="catalog__range__head">
                    <span className="catalog__label tp-xs semibold">
                      {t("catalog:filters:pointsRange:label")}
                    </span>
                    <span className="catalog__range__value tp-xs light">
                      {t("catalog:filters:pointsRange:value", {
                          min: formatPoints(state.minPoints ?? 0),
                          max: formatPoints(state.maxPoints ?? 0),
                      })}
                    </span>
                </div>

                <div className="range-slider" role="group" aria-label={t("catalog:filters:pointsRange:label")}>
                    <Controller
                        control={control}
                        name="minPoints"
                        render={({field}) => (
                            <input
                                className="range-slider__input"
                                type="range"
                                min={minBound}
                                max={maxBound}
                                step={step}
                                value={Number(field.value ?? minBound)}
                                onChange={(e) => {
                                    const nextMin = Math.min(
                                        Number(e.target.value),
                                        Number(getValues("maxPoints") ?? maxBound)
                                    );
                                    field.onChange(nextMin);
                                }}
                                aria-label={t("catalog:filters:pointsRange:min")}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="maxPoints"
                        render={({field}) => (
                            <input
                                className="range-slider__input"
                                type="range"
                                min={minBound}
                                max={maxBound}
                                step={step}
                                value={Number(field.value ?? maxBound)}
                                onChange={(e) => {
                                    const nextMax = Math.max(
                                        Number(e.target.value),
                                        Number(getValues("minPoints") ?? minBound)
                                    );
                                    field.onChange(nextMax);
                                }}
                                aria-label={t("catalog:filters:pointsRange:max")}
                            />
                        )}
                    />

                    <div className="range-slider__track" aria-hidden="true">
                        <div
                            className="range-slider__track__selected"
                            style={{
                                left: `${((Number(minPoints ?? minBound) - minBound) / Math.max(1, maxBound - minBound)) * 100}%`,
                                right: `${(1 - (Number(maxPoints ?? maxBound) - minBound) / Math.max(1, maxBound - minBound)) * 100}%`,
                            }}
                        />
                    </div>
                </div>

            </div>
        </aside>
    );
}

function gcd(a: number, b: number) {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b !== 0) {
        const t = b;
        b = a % b;
        a = t;
    }
    return a;
}

export function getAlignedNiceStep(min: number, max: number) {
    const span = Math.max(0, max - min);
    if (span <= 0) return 1;

    const nice = getNiceStep(min, max);
    if (span % nice === 0) return nice;

    const candidates = [
        nice,
        Math.floor(nice / 2),
        Math.floor(nice / 4),
        Math.floor(nice / 5),
        Math.floor(nice / 10),
    ]
        .map((n) => Math.max(1, n))
        .filter((n, i, arr) => arr.indexOf(n) === i)
        .sort((a, b) => b - a);

    for (const s of candidates) {
        if (span % s === 0) return s;
    }

    const g = gcd(span, nice);
    if (g > 1) return g;

    return 1;
}

export function getNiceStep(min: number, max: number) {
    const span = Math.max(0, max - min);
    if (span <= 0) return 1;

    const raw = span / 200;

    const pow10 = Math.pow(10, Math.floor(Math.log10(raw)));
    const n = raw / pow10;

    const base = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;

    const step = base * pow10;

    return Math.max(1, Math.round(step));
}

export function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}